import {
  MS_PER_DAY,
  TRESPONSE_LLBoardListing,
  TRESPONSE_TNA_BOARD_LISTING,
  TTimelineDateRangeDTO,
} from '@monorepo/types';
import { TTimelineModel } from './timeline.types';
import { DateUtils } from '@monorepo/utils';

export const getStripColorPatternMap = (
  tnaRes: TRESPONSE_TNA_BOARD_LISTING['stripStatusMetadataMap']['COMPLETED_ON_TIME']['stripColor'],
) => {
  switch (tnaRes) {
    case 'GREY': {
      return {
        color: '#A3A3A7',
        hasMask: false,
        iconId: tnaRes,
      };
    }
    case 'GREEN':
    case 'GREEN_PATTERN': {
      return {
        color: '#9DDFB9',
        hasMask: tnaRes === 'GREEN_PATTERN',
        iconId: tnaRes,
      };
    }
    case 'RED':
    case 'RED_PATTERN': {
      return {
        color: '#FF6140',
        hasMask: tnaRes === 'RED_PATTERN',
        iconId: tnaRes,
      };
    }
    case 'YELLOW':
    case 'YELLOW_PATTERN': {
      return {
        color: '#FFE86C',
        borderColor: 'hsla(57, 100%, 31%, 1)',
        hasMask: tnaRes === 'YELLOW_PATTERN',
        iconId: tnaRes,
      };
    }
    default: {
      return {
        color: 'black',
        hasMask: false,
        iconId: tnaRes,
      };
    }
  }
};

type TColorPalette = {
  color: string;
  hasMask: boolean;
};

type TStripColor =
  TRESPONSE_LLBoardListing['llBoardDTO']['llBoardByFactoryList'][number]['llBoardLines'][number]['stripInfoList'][number]['stripColor'];

const colorMap: Record<TStripColor, TColorPalette> = {
  GREEN: { color: '#9DDFB9', hasMask: false },
  RED: { color: '#FF6140', hasMask: false },
  YELLOW: { color: '#FFE86C', hasMask: false },
  BLUE: { color: '#95C7F4', hasMask: false },
  MELON_ORANGE: { color: '#F2B6A9', hasMask: false },
  LIGHT_PASTEL_PURPLE: { color: '#D0C4E8', hasMask: false },
  WATERSPOUT_BLUE: { color: '#9DF2F8', hasMask: false },
  BEAU_BLUE: { color: '#BADBF8', hasMask: false },
  SECONDARY_YELLOW_05: { color: '#A7830B', hasMask: false },
  SECONDARY_YELLOW_04: { color: '#D7A90E', hasMask: false },
  SECONDARY_YELLOW_03: { color: '#F4D058', hasMask: false },
  SECONDARY_YELLOW_02: { color: '#F7DD87', hasMask: false },
  SECONDARY_YELLOW_01: { color: '#FAEBB7', hasMask: false },
  ORANGE_05: { color: '#643A02', hasMask: false },
  ORANGE_04: { color: '#C87304', hasMask: false },
  ORANGE_03: { color: '#FA9005', hasMask: false },
  ORANGE_02: { color: '#FCBC69', hasMask: false },
  ORANGE_01: { color: '#FEE9CD', hasMask: false },
  TEAL_06: { color: '#0A3D2F', hasMask: false },
  TEAL_05: { color: '#116A51', hasMask: false },
  TEAL_04: { color: '#1FC295', hasMask: false },
  TEAL_03: { color: '#61E6C1', hasMask: false },
  TEAL_02: { color: '#BAF4E4', hasMask: false },
  TEAL_01: { color: '#E5FBF5', hasMask: false },
  GREY: { color: '#EAEEF0', hasMask: false },
  PURPLE_06: { color: '#5F3EA2', hasMask: false },
  PURPLE_05: { color: '#7755BE', hasMask: false },
  PURPLE_04: { color: '#9479CC', hasMask: false },
  PURPLE_03: { color: '#B29FDA', hasMask: false },
  PURPLE_01: { color: '#EDE9F7', hasMask: false },
  BLUE_08: { color: '#D6DFFF', hasMask: false },
  BLUE_07: { color: '#5277FF', hasMask: false },
  BLUE_06: { color: '#003A8C', hasMask: false },
  BLUE_05: { color: '#012566', hasMask: false },
  BLUE_04: { color: '#0169D1', hasMask: false },
  BLUE_03: { color: '#1994FF', hasMask: false },
  BLUE_02: { color: '#66C2FF', hasMask: false },
  BLUE_01: { color: '#0A8DD0', hasMask: false },
  RED_05: { color: '#B3321E', hasMask: false },
  RED_04: { color: '#872617', hasMask: false },
  RED_03: { color: '#E36755', hasMask: false },
  RED_02: { color: '#E36755', hasMask: false },
  RED_01: { color: '#EA8E80', hasMask: false },
  GREEN_03: { color: '#16E43F', hasMask: false },
  GREEN_02: { color: '#6FF189', hasMask: false },
  BLACK: { color: '#333333', hasMask: false },
};

const getColorPalette = (color: TStripColor, defaultColor: TColorPalette): TColorPalette => {
  return colorMap[color] || defaultColor;
};

export const getLlStripColorPallet = (color: TStripColor): TColorPalette => {
  return getColorPalette(color, { color: '#EAEEF0', hasMask: false });
};

export const getLlBorderColorPallet = (color: TStripColor): TColorPalette => {
  return getColorPalette(color, { color: '#636267', hasMask: false });
};

export const getLlTextColorPallet = (color: TStripColor): TColorPalette => {
  return getColorPalette(color, { color: '#222222', hasMask: false });
};

export const getLeftNavBackgroundColour = (index: number, color1: string, color2: string): string => {
  return index % 2 === 0 ? color1 : color2;
};

export function getTotalDaysInTimeline(timelineModel: TTimelineModel) {
  const timelineStart = timelineModel.timeline[0].date;
  const timelineEnd = timelineModel.timeline[timelineModel.timeline.length - 1].date;
  return DateUtils.dateRangeDiff({ to: timelineEnd, from: timelineStart }, 'days') + 1;
}

export function getAvailableDateRangeInTimeline(timelineModel: TTimelineModel): TTimelineDateRangeDTO {
  const timeline = timelineModel.timeline;
  const dataFrom = timeline[0].date;
  const dataTo = timeline[timeline.length - 1].date;

  return { from: dataFrom, to: dataTo };
}

/**
 * Adjusts a date range by the requested number of days while respecting
 * minimum, maximum, and available range boundaries.
 *
 * @param currentDateRange - The current visible date range.
 * @param daysDelta - Number of days to add or remove. Negative values zoom in,
 *   positive values zoom out.
 * @param availableDateRange - The date range that the result must fit within.
 * @param maxDays - Maximum number of days allowed in the result.
 * @param minDays - Minimum number of days allowed in the result. Defaults to 3.
 * @param anchorDate - Optional date whose relative position in the current range
 *   should be preserved. If omitted or invalid, the range is anchored at its middle.
 *
 * The requested change is applied as much as possible while respecting all
 * boundaries. If the anchor or resulting range reaches an available boundary,
 * the range is shifted as little as necessary to fit.
 */
export function timelineZoomDateRange(
  currentDateRange: TTimelineDateRangeDTO,
  daysDelta: number,
  availableDateRange: TTimelineDateRangeDTO,
  maxDays: number,
  minDays = 3,
  anchorDate?: string,
): TTimelineDateRangeDTO {
  if (daysDelta === 0) return currentDateRange;

  const currentFrom = Date.parse(currentDateRange.from);
  const currentTo = Date.parse(currentDateRange.to);
  const availableFrom = Date.parse(availableDateRange.from);
  const availableTo = Date.parse(availableDateRange.to);

  const currentDays = (currentTo - currentFrom) / MS_PER_DAY + 1;
  const availableDays = (availableTo - availableFrom) / MS_PER_DAY + 1;

  const effectiveMinDays = Math.min(minDays, availableDays);
  const effectiveMaxDays = Math.min(maxDays, availableDays);

  // Apply the requested delta, but clamp it to the valid range.
  const targetDays = Math.min(Math.max(currentDays + daysDelta, effectiveMinDays), effectiveMaxDays);

  if (targetDays === currentDays) return currentDateRange;

  const targetSpan = targetDays - 1;

  let anchor: number;

  const parsedAnchor = anchorDate ? Date.parse(anchorDate) : NaN;

  // Use the provided anchor only when it is valid and inside
  // both the current and available ranges.
  if (
    Number.isFinite(parsedAnchor) &&
    parsedAnchor >= currentFrom &&
    parsedAnchor <= currentTo &&
    parsedAnchor >= availableFrom &&
    parsedAnchor <= availableTo
  ) {
    anchor = parsedAnchor;
  } else {
    // no anchor: use the middle of the current range.
    const currentSpan = currentDays - 1;
    anchor = currentFrom + Math.floor(currentSpan / 2) * MS_PER_DAY;
  }

  const currentSpan = currentDays - 1;

  const anchorPosition = currentSpan === 0 ? 0 : (anchor - currentFrom) / MS_PER_DAY / currentSpan;

  const daysBeforeAnchor = Math.round(targetSpan * anchorPosition);
  const daysAfterAnchor = targetSpan - daysBeforeAnchor;

  let from = anchor - daysBeforeAnchor * MS_PER_DAY;
  let to = anchor + daysAfterAnchor * MS_PER_DAY;

  // Shift the range as little as necessary to fit within
  // the available range.
  if (from < availableFrom) {
    const shift = availableFrom - from;
    from += shift;
    to += shift;
  }

  if (to > availableTo) {
    const shift = to - availableTo;
    from -= shift;
    to -= shift;
  }

  return {
    from: new Date(from).toISOString().slice(0, 10),
    to: new Date(to).toISOString().slice(0, 10),
  };
}

export function timelineResetZoomDateRange(
  currentDateRange: TTimelineDateRangeDTO,
  availableDateRange: TTimelineDateRangeDTO,
  maxDays: number,
): TTimelineDateRangeDTO {
  const currentFrom = Date.parse(currentDateRange.from);
  const currentTo = Date.parse(currentDateRange.to);
  const availableFrom = Date.parse(availableDateRange.from);
  const availableTo = Date.parse(availableDateRange.to);

  const currentDays = (currentTo - currentFrom) / MS_PER_DAY + 1;
  const availableDays = (availableTo - availableFrom) / MS_PER_DAY + 1;

  const targetDays = Math.min(maxDays, availableDays);

  if (targetDays <= currentDays) return currentDateRange;

  return timelineZoomDateRange(currentDateRange, targetDays - currentDays, availableDateRange, maxDays);
}
